import React, { useCallback, useState, useEffect } from "react";
import styled from "styled-components";

import {
  FlexColumnContainer,
  FlexRowContainer,
  useWindowDimensions,
} from "@trailhead/core-ui";
import {
  Button as AsurionButton,
  ButtonGroup,
  Callout,
  Dropdown,
  DropdownOptions,
  Icon,
  Size,
  Text,
  breakpoints,
  colors,
} from "@trailhead/asurion-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { trackEvent, updateStickyProps } from "@trailhead/analytics";
import {
  FALLBACK_DEVICE_LIST,
  FALLBACK_DEVICE_QUESTION_LABELS,
  FALLBACK_DEVICE_QUESTION_LABELS_ATT,
  MobileDeviceValue,
  MobileDeviceValues,
  useNavigate,
  useQueryParams,
} from "@trailhead/smart-setup-core";
import { useDeviceState } from "@trailhead/global-device-context";
import { PartnerType } from "@trailhead/contentful";
import { useMxContext } from "@trailhead/mx-context/src";

const SmartSetupContentContainer = styled(FlexColumnContainer)`
  padding: 2.625rem 0.5rem 0rem 0.5rem;
  position: relative;

  ${breakpoints.md} {
    padding: 3.25rem 1.125rem 2.375rem 1.125rem;
  }

  ${breakpoints.lg} {
    max-width: 45.1875rem;
  }
`;

const MainHeaderContainer = styled(FlexColumnContainer)`
  gap: 0.6875rem;
  margin-bottom: 1.5rem;

  ${breakpoints.md} {
    margin-bottom: unset;
  }
`;

const HeaderContainer = styled(FlexColumnContainer)`
  padding-left: 0.5rem;

  ${breakpoints.md} {
    width: 42rem;
    padding: 0rem 1.125rem 0rem 1rem;
  }
`;

const HeaderTitle = styled(Text)``;

const EstimatedTimeHeaderContainer = styled(FlexColumnContainer)`
  padding: 0rem 0rem 2.375rem 0.5rem;
  border-bottom: 0.0625rem solid ${colors.neutralBrighter};

  ${breakpoints.md} {
    padding: 0rem 1rem 1.375rem 1rem;
    flex-direction: row;
  }
`;

const EstimatedTime = styled(FlexRowContainer)`
  gap: 0.4375rem;
  align-items: center;

  ${breakpoints.md} {
    gap: 0.9375rem;
    padding-top: unset;
  }
`;

const EstimatedTimeInfo = styled(Text)``;
const EstimatedTimeIcon = styled(Icon)``;

const MainContentContainer = styled(FlexColumnContainer)`
  padding: 2.25rem 0.5rem;
  gap: 2.25rem;

  ${breakpoints.md} {
    padding: 3rem 1rem 2.25rem 1rem;
    gap: 2.5rem;
  }
`;

const QuestionText = styled(Text)``;
const DropdownContainer = styled(FlexColumnContainer)`
  ${breakpoints.md} {
    width: 21.4375rem;
  }
`;

const LatestIphoneAndCalloutContainer = styled(FlexColumnContainer)`
  gap: 0.9375rem;

  ${breakpoints.lg} {
    white-space: nowrap;
  }
`;

const LatestIphoneQuestionContainer = styled(FlexColumnContainer)`
  gap: 1.4375rem;

  ${breakpoints.md} {
    gap: 3.1875rem;
  }
`;

const CalloutWrapper = styled(Callout)`
  p {
    font-size: 0.875rem;
  }

  ${breakpoints.md} {
    margin-right: 3.25rem;
  }
`;

const ContinueButtonContainer = styled(FlexColumnContainer)`
  padding: 0rem 0.625rem 0.625rem 0.625rem;

  ${breakpoints.md} {
    width: 11.625rem;
  }
`;

const ButtonGroupWrapper = styled(ButtonGroup)`
  width: 16.125rem;
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  height: 2.8125rem;
  padding: 0.75rem 1.125rem;
`;

interface DeviceQuestionProps {
  id: string;
  options: DropdownOptions[];
  question: JSX.Element;
  label: string;
  value: string;
  onChange: (
    event:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>,
  ) => void;
}

export const SmartSetupContent: React.FC<{ cta: JSX.Element | null }> = ({
  cta = null,
}) => {
  const t = useSanitizedTranslation();
  const { client } = useMxContext();

  const {
    oldDeviceFunctional,
    newDeviceObName,
    oldDeviceObName,
    oldDeviceAccess,
    newDeviceActivated,
    updateOldDeviceFunctional,
    updateOldDeviceAccess,
    updateOldDeviceObName,
    updateNewDeviceObName,
    newDeviceIsiPhone14,
    updateNewDeviceIsiPhone14,
  } = useDeviceState();

  const [newDevice, setNewDevice] = useState<string>(newDeviceObName ?? "");
  const [oldDevice, setOldDevice] = useState<string>(oldDeviceObName ?? "");

  const { width } = useWindowDimensions();
  const isMobile = width < Size.MD;

  const deviceLists = FALLBACK_DEVICE_LIST;

  const showiPhone14Prompt = [PartnerType.VERIZON,PartnerType.LIBERTY, PartnerType.US_CELLULAR].includes(client as unknown as PartnerType);

  const questionLabels = client === PartnerType.ATT ? FALLBACK_DEVICE_QUESTION_LABELS_ATT: FALLBACK_DEVICE_QUESTION_LABELS;


  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const cid = queryParams.get("cid");
  const oldDeviceNameNotAvailable = "N/A";
  let oldDeviceList = deviceLists.oldDeviceList;

  if (cid === "vz_bu_ss") {
    updateOldDeviceAccess(true);
    updateOldDeviceFunctional(true);
    oldDeviceList = oldDeviceList.filter(
      ({ name }) => name !== oldDeviceNameNotAvailable,
    );
  }

  const onContinueClick = () => {
    const analyticsObj = {
      oldDeviceFunctional,
      oldDeviceAccess,
      oldDeviceObName,
      newDeviceObName,
      newDeviceActivated,
    };
    trackEvent("Intro_Page_Completed_Click", analyticsObj);
    updateStickyProps(analyticsObj);
    navigate("/mobile-device-setup/tutorials");
  };

  const handleNewDeviceUpdate = useCallback(
    (device: string) => {
      setNewDevice(device);
      updateNewDeviceObName(
        (device as MobileDeviceValue) === MobileDeviceValues.Pixel
          ? MobileDeviceValues.OtherAndroid
          : (device as MobileDeviceValue),
      );
      trackEvent("Replacement_Device_Selected_Click", {
        newDevice: device,
      });
    },
    [updateNewDeviceObName],
  );

  const handleOldDeviceUpdate = useCallback(
    (device: string) => {
      setOldDevice(device);
      updateOldDeviceObName(
        (device as MobileDeviceValue) === MobileDeviceValues.Pixel
          ? MobileDeviceValues.OtherAndroid
          : (device as MobileDeviceValue),
      );
      trackEvent("Old_Device_Selected_Click", { oldDevice: device });

      if (device === oldDeviceNameNotAvailable) {
        updateOldDeviceAccess(false);
        updateOldDeviceFunctional(false);
      }
    },
    [
      updateOldDeviceObName,
      updateOldDeviceAccess,
      updateOldDeviceFunctional,
      setOldDevice,
    ],
  );

  const onUpdateNewDeviceIsiPhone14 = (value: boolean) => {
    trackEvent("Device_Is_iPhone14_Click", {
      isIphone14: value,
    });
    updateNewDeviceIsiPhone14(value);
  };

  const DeviceQuestion: React.FC<DeviceQuestionProps> = ({
    id,
    onChange,
    options,
    label,
    value,
    question,
  }) => (
    <>
      <QuestionText size={4}>{question}</QuestionText>
      <DropdownContainer>
        <Dropdown
          id={id}
          options={options}
          label={t(label)}
          value={value}
          onChange={onChange}
        />
      </DropdownContainer>
    </>
  );

  const CalloutComponent: React.FC = () => (
    <CalloutWrapper
      content={t(
        "Our customized guides will help walk you through step by step. You'll need let us know your devices before we can assist you with your setup.",
      )}
      heading=""
    />
  );

  const shouldRenderCallout = (show: boolean): JSX.Element | null =>
    show ? <CalloutComponent /> : null;

  useEffect(() => {
    trackEvent("Page_Loaded", {
      pageName: "Intro_Page",
    });
  }, []);

  return (
    <SmartSetupContentContainer>
      <MainHeaderContainer>
        <HeaderContainer>
          <HeaderTitle weight="feather" size={isMobile ? 5 : 8}>
            {t("Welcome to Smart Setup")}
          </HeaderTitle>
        </HeaderContainer>
        <EstimatedTimeHeaderContainer>
          <EstimatedTime>
            <EstimatedTimeIcon
              src="time"
              size={isMobile ? "normal" : "xlarge"}
              fill={colors.neutralDeeper}
            />
            <EstimatedTimeInfo
              size={isMobile ? 1 : 3}
              color={colors.neutralDeeper}
            >
              {t("10-25 minutes to complete")}
            </EstimatedTimeInfo>
          </EstimatedTime>
        </EstimatedTimeHeaderContainer>
      </MainHeaderContainer>
      {isMobile ? cta : null}
      <MainContentContainer>
        {shouldRenderCallout(isMobile)}
        <DeviceQuestion
          id={"NewDeviceDropdown"}
          question={
            <>
              {t("What brand is your")}{" "}
              <Text size={4} weight="heavy">
                {t("new")}
              </Text>{" "}
              {t("phone?")}
            </>
          }
          options={deviceLists.newDeviceList}
          label={"Select your replacement device"}
          value={newDevice}
          onChange={(
            event:
              | React.ChangeEvent<HTMLSelectElement>
              | React.ChangeEvent<HTMLInputElement>,
          ) => handleNewDeviceUpdate(event.currentTarget.value)}
        />
        {showiPhone14Prompt && newDeviceObName === MobileDeviceValues.iPhone ? (
          <LatestIphoneAndCalloutContainer>
            <LatestIphoneQuestionContainer>
              <QuestionText size={4}>
                {t(questionLabels.newDeviceIsNewiPhoneVersion)}
              </QuestionText>
              <ButtonGroupWrapper alignment="start" hasFullWidthButtons>
                <AsurionButtonWrapper
                  onClick={() => onUpdateNewDeviceIsiPhone14(true)}
                  size="medium"
                  variant={
                    newDeviceIsiPhone14 === null || !newDeviceIsiPhone14
                      ? "outline"
                      : "default"
                  }
                  data-testid="is-new-device-iphone14-button-yes"
                >
                  <Text size={2} weight="heavy">
                    {t("Yes")}
                  </Text>
                </AsurionButtonWrapper>
                <AsurionButtonWrapper
                  onClick={() => onUpdateNewDeviceIsiPhone14(false)}
                  size="medium"
                  variant={
                    newDeviceIsiPhone14 === null || newDeviceIsiPhone14
                      ? "outline"
                      : "default"
                  }
                  data-testid="is-new-device-iphone14-button-no"
                >
                  <Text size={2} weight="heavy">
                    {t("No")}
                  </Text>
                </AsurionButtonWrapper>
              </ButtonGroupWrapper>
            </LatestIphoneQuestionContainer>
            {shouldRenderCallout(!isMobile)}
          </LatestIphoneAndCalloutContainer>
        ) : (
          shouldRenderCallout(!isMobile)
        )}
        <DeviceQuestion
          id={"OldDeviceDropdown"}
          question={
            <>
              {t("What brand is your")}{" "}
              <Text size={4} weight="heavy">
                {t("old")}
              </Text>{" "}
              {t("device?")}
            </>
          }
          options={oldDeviceList}
          label={"Select your old device"}
          value={oldDevice}
          onChange={(
            event:
              | React.ChangeEvent<HTMLSelectElement>
              | React.ChangeEvent<HTMLInputElement>,
          ) => handleOldDeviceUpdate(event.currentTarget.value)}
        />
      </MainContentContainer>
      <ContinueButtonContainer>
        <AsurionButtonWrapper
          disabled={!newDeviceObName || !oldDeviceObName}
          onClick={onContinueClick}
          size="medium"
          variant="default"
          iconSide="right"
          iconSrc="ArrowRight"
        >
          {t("Continue")}
        </AsurionButtonWrapper>
      </ContinueButtonContainer>
    </SmartSetupContentContainer>
  );
};
